import { computed, reactive } from 'vue'
import type { FormSoftMessage } from '@/services/form-events/formSoftEvent'

/** Сообщения soft form-events по slotId формы (pagesData key). */
const messagesBySlot = reactive(new Map<string, FormSoftMessage[]>())
/** Waiters: action pipeline ждёт, пока пользователь закроет soft-message на слоте. */
const waitersBySlot = new Map<string, Array<() => void>>()

function listForSlot(slotId: string): FormSoftMessage[] {
  const key = String(slotId ?? '').trim()
  if (!key) return []
  return messagesBySlot.get(key) ?? []
}

export function getFormSoftMessages(slotId: string): FormSoftMessage[] {
  return listForSlot(slotId)
}

export function hasFormSoftMessages(slotId: string | null | undefined): boolean {
  return listForSlot(String(slotId ?? '')).length > 0
}

/**
 * Блокирует вызывающий код, пока на слоте есть soft-messages.
 * Снимается после hide/reload/close по кнопке на балуне.
 */
export function waitUntilFormSoftMessagesCleared(
  slotId: string | null | undefined,
): Promise<void> {
  const key = String(slotId ?? '').trim()
  if (!key || !hasFormSoftMessages(key)) {
    return Promise.resolve()
  }
  return new Promise((resolve) => {
    const list = waitersBySlot.get(key) ?? []
    list.push(resolve)
    waitersBySlot.set(key, list)
  })
}

function flushWaiters(slotId: string): void {
  const key = String(slotId ?? '').trim()
  if (!key || hasFormSoftMessages(key)) return
  const list = waitersBySlot.get(key) ?? []
  waitersBySlot.delete(key)
  list.forEach((resolve) => resolve())
}

/** Reactive list for a form slot (use in FormMessagesOverlay). */
export function useFormSoftMessages(slotId: () => string | null | undefined) {
  return computed(() => {
    const id = String(slotId() ?? '').trim()
    if (!id) return [] as FormSoftMessage[]
    // touch Map for reactivity
    void messagesBySlot.size
    return messagesBySlot.get(id) ?? []
  })
}

/**
 * Добавить сообщение. Dedupe по eventId (Swing FormMessage.equals).
 */
export function addFormSoftMessage(message: FormSoftMessage): void {
  const slotId = String(message.slotId ?? '').trim()
  if (!slotId) return

  const existing = messagesBySlot.get(slotId) ?? []
  if (message.eventId) {
    const idx = existing.findIndex((m) => m.eventId != null && m.eventId === message.eventId)
    if (idx >= 0) {
      const next = existing.slice()
      next[idx] = message
      messagesBySlot.set(slotId, next)
      return
    }
  }
  messagesBySlot.set(slotId, [...existing, message])
}

export function removeFormSoftMessage(slotId: string, messageId: string): void {
  const key = String(slotId ?? '').trim()
  if (!key) return
  const existing = messagesBySlot.get(key)
  if (!existing?.length) return
  const next = existing.filter((m) => m.id !== messageId)
  if (next.length === 0) {
    messagesBySlot.delete(key)
  } else {
    messagesBySlot.set(key, next)
  }
  flushWaiters(key)
}

export function clearFormSoftMessages(slotId: string): void {
  const key = String(slotId ?? '').trim()
  if (!key) return
  messagesBySlot.delete(key)
  flushWaiters(key)
}

/** @internal */
export function __resetFormSoftMessagesForTests(): void {
  messagesBySlot.clear()
  waitersBySlot.clear()
}
